import { enqueueSnackbar } from 'notistack';
import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { IconEye, IconEyeOff } from '@tabler/icons-react';

import Button from '@/components/Button/Button';
import Input from '@/components/Input/Input';
import SectionLoader from '@/components/Loader/SectionLoader';
import { useFormatText } from '@/hooks/useFormatText';
import {
  TResetPassword,
  resetPasswordSchema,
} from '@/schema/resetPasswordSchema';
import { resetPasswordApi, resetLinkVerification } from '@/service/authService';
import { encryptPassword } from '@/utils/encryption';
import { zodResolver } from '@hookform/resolvers/zod';

import LinkExpired from './LinkExpired';

const ResetPassword = () => {
  const { formatText } = useFormatText();
  const [show, setShow] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const navigate = useNavigate();
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isLinkExpired, setIsLinkExpired] = useState<boolean | null>(null);
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<TResetPassword>({
    resolver: zodResolver(resetPasswordSchema),
  });
  const password = watch('password');

  // Validate token
  useEffect(() => {
    if (!token) {
      setIsLinkExpired(true);
      return;
    }
    const checkLinkStatus = async () => {
      setLoading(true);
      try {
        const response = await resetLinkVerification({ token });
        if (response?.status) {
          setIsLinkExpired(false);
        } else {
          setIsLinkExpired(true);
        }
      } catch (error) {
        console.error('Error checking link status:', error);
        setIsLinkExpired(true);
      } finally {
        setLoading(false);
      }
    };
    checkLinkStatus();
  }, [searchParams, token]);

  // Password requirements data
  const passwordRequirements = [
    {
      id: 'minLength',
      text: formatText('reset-password.form.validations.min-length'),
      isValid: password && password.length >= 8,
    },
    {
      id: 'uppercase',
      text: formatText('reset-password.form.validations.uppercase'),
      isValid: password && /[A-Z]/.test(password),
    },
    {
      id: 'lowercase',
      text: formatText('reset-password.form.validations.lowercase'),
      isValid: password && /[a-z]/.test(password),
    },
    {
      id: 'number',
      text: formatText('reset-password.form.validations.number'),
      isValid: password && /\d/.test(password),
    },
  ];

  // Check if all requirements are met
  const allPasswordRequirementsMet = passwordRequirements.every(
    (req) => req.isValid
  );

  const onSubmit = async (formData: TResetPassword) => {
    setHasSubmitted(true);

    if (!token) {
      return;
    }

    if (!allPasswordRequirementsMet) {
      setHasSubmitted(false);
      return;
    }

    setLoading(true);
    try {
      const payload: { token: string; password: string } = {
        token,
        password: encryptPassword(formData.password),
      };

      const response = await resetPasswordApi(payload);

      if (response?.status) {
        enqueueSnackbar(response?.message, { variant: 'success' });
        navigate('/success');
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
      setHasSubmitted(false);
    }
  };

  if (isLinkExpired === null || loading) {
    return <SectionLoader />;
  }

  if (isLinkExpired) {
    return <LinkExpired token={token} />;
  }

  return (
    <div className="flex flex-col justify-center p-15 md:p-10 w-full lg:max-w-150">
      <div className="w-full h-full flex flex-col items-center my-auto">
        <p className="pb-2 text-center  text-4xl max-md:text-3xl font-bold text-neutral-primary">
          {formatText('reset-password.title')}
        </p>
        <p className="text-neutral-secondary text-center text-lg font-medium pb-10 max-md:pb-6">
          {formatText('reset-password.sub-title')}
        </p>
        <form
          method="POST"
          className="w-full"
          onSubmit={handleSubmit(onSubmit)}
        >
          <div className="flex flex-col gap-4">
            <Input
              name="password"
              type={show ? 'text' : 'password'}
              placeholder={formatText(
                'reset-password.form.password-placeholder'
              )}
              register={register}
              isRequired
              rightIcon={{
                icon: show ? (
                  <IconEyeOff
                    size={20}
                    color="#99A0AE"
                    className="cursor-pointer"
                  />
                ) : (
                  <IconEye
                    size={20}
                    color="#99A0AE"
                    className="cursor-pointer"
                  />
                ),
                onClick: () => setShow((prev) => !prev),
              }}
              error={
                errors.password?.message
                  ? formatText(errors.password.message)
                  : undefined
              }
              rightIconBg={'#ffffff'}
              label={formatText('reset-password.form.password')}
            />
            <Input
              name="confirmPassword"
              type={showConfirm ? 'text' : 'password'}
              placeholder={formatText(
                'reset-password.form.confirm-password-placeholder'
              )}
              register={register}
              isRequired
              rightIcon={{
                icon: showConfirm ? (
                  <IconEyeOff
                    size={20}
                    color="#99A0AE"
                    className="cursor-pointer"
                  />
                ) : (
                  <IconEye
                    size={20}
                    color="#99A0AE"
                    className="cursor-pointer"
                  />
                ),
                onClick: () => setShowConfirm((prev) => !prev),
              }}
              error={
                errors.confirmPassword?.message
                  ? formatText(errors.confirmPassword.message)
                  : undefined
              }
              rightIconBg={'#ffffff'}
              label={formatText('reset-password.form.confirm-password')}
            />
          </div>
          {/* Password Requirements */}
          <div className="mt-4 space-y-2">
            {passwordRequirements.map((requirement) => (
              <div key={requirement.id} className="flex items-center space-x-2">
                <div
                  className={`w-1 h-1 rounded-full ${
                    requirement.isValid
                      ? 'bg-success-secondary'
                      : hasSubmitted
                        ? 'bg-red-600'
                        : 'bg-[#8B8B8B]'
                  }`}
                />
                <span
                  className={`text-sm ${
                    requirement.isValid
                      ? 'text-success-secondary'
                      : hasSubmitted
                        ? 'text-red-600'
                        : 'text-[#8B8B8B]'
                  }`}
                >
                  {requirement.text}
                </span>
              </div>
            ))}
          </div>
          <Button
            label={formatText('reset-password.form.actions.reset-password')}
            type="submit"
            variant="primary"
            loading={loading}
            className="w-full py-3.5! text-[1rem] leading-[150%] mt-6"
          />
        </form>
      </div>
    </div>
  );
};

export default ResetPassword;
